import { type FieldValues, useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import {
  DropdownSingleSelect,
  type DropdownSingleSelectProps,
} from '~/chords/DropdownSingleSelect/DropdownSingleSelect';
import { FormField, type FormFieldProps } from '~/chords/FormField';

type FormFieldDropdownSingleSelectProps<
  TOptionProps,
  TFormFields extends FieldValues,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<
    DropdownSingleSelectProps<TOptionProps>,
    'name' | 'className' | 'value' | 'onValueChange'
  > & {
    onValueChange?: (value: string) => void;
  };

export function FormFieldDropdownSingleSelect<
  TOptionProps = unknown,
  TFormFields extends FieldValues = FieldValues,
>({
  name,
  label,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  labelAddon,
  fieldMessageClassName,
  onValueChange,
  showErrorMessage = true,
  ...dropdownProps
}: ComponentProps<
  FormFieldDropdownSingleSelectProps<TOptionProps, TFormFields>
>) {
  const {
    field: { ref: _ref, ...fieldWithoutRef },
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      fieldMessageClassName={fieldMessageClassName}
      showErrorMessage={showErrorMessage}
    >
      <DropdownSingleSelect
        id={name}
        {...fieldWithoutRef}
        onValueChange={(value) => {
          fieldWithoutRef.onChange(value);
          onValueChange?.(value);
        }}
        disabled={disabled || readOnly}
        state={invalid ? 'error' : 'default'}
        {...dropdownProps}
      />
    </FormField>
  );
}

FormFieldDropdownSingleSelect.displayName = 'FormFieldDropdownSingleSelect';
